/**
 * PGLP | MONTE HERMOM
 * UTIL: classificarTermoRegistral
 * ------------------------------------------------------------
 * Classifica uma string de tipologia registral contra a base
 * de termos (vinda do backend).
 *
 * Retorna:
 *   { categoria, uso, tipologia, match, termoOrigem }
 * ============================================================
 */

/**
 * Normaliza texto: lowercase + remove acentos + trim.
 * Precisa bater com o `termo_normalizado` do banco.
 */
export function normalizarTermo(texto) {
  return (texto || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove diacríticos
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Classifica o termo registral.
 *
 * @param {string} texto — texto digitado pelo perito
 * @param {Array}  termos — lista vinda do hook useTermosRegistrais
 * @returns {{
 *   categoria: 'ESPECIFICO' | 'GENERICO_COM_FINALIDADE' | 'GENERICO_PURO' | 'SOLO' | 'DESCONHECIDO' | 'VAZIO',
 *   uso: string | null,
 *   tipologia: string | null,
 *   match: 'EXATO' | 'PARCIAL' | null,
 *   termoOrigem: string | null,
 * }}
 */
export function classificarTermoRegistral(texto, termos = []) {
  /* ---------- Vazio ---------- */
  if (!texto || !texto.trim()) {
    return { categoria: 'VAZIO', uso: null, tipologia: null, match: null, termoOrigem: null };
  }

  /* ---------- Sem base de termos ---------- */
  if (!Array.isArray(termos) || termos.length === 0) {
    return { categoria: 'DESCONHECIDO', uso: null, tipologia: null, match: null, termoOrigem: null };
  }

  const alvo = normalizarTermo(texto);

  /* ---------- Match 1: EXATO ---------- */
  const exato = termos.find((t) => t.termo_normalizado === alvo);
  if (exato) {
    return {
      categoria:   exato.categoria,
      uso:         exato.uso_inferido    || null,
      tipologia:   exato.tipologia_inferida || null,
      match:       'EXATO',
      termoOrigem: exato.termo,
    };
  }

  /* ---------- Match 2: PARCIAL ----------
   * Ordena por tamanho decrescente — termos mais longos
   * têm prioridade (ex: "prédio residencial" antes de "prédio").
   */
  const ordenado = [...termos].sort(
    (a, b) => b.termo_normalizado.length - a.termo_normalizado.length
  );

  for (const t of ordenado) {
    if (alvo.includes(t.termo_normalizado)) {
      return {
        categoria:   t.categoria,
        uso:         t.uso_inferido      || null,
        tipologia:   t.tipologia_inferida || null,
        match:       'PARCIAL',
        termoOrigem: t.termo,
      };
    }
  }

  /* ---------- Sem match ---------- */
  return {
    categoria:   'DESCONHECIDO',
    uso:         null,
    tipologia:   null,
    match:       null,
    termoOrigem: null,
  };
}

/**
 * Mapa de labels para exibir na UI.
 */
export const CATEGORIA_LABELS = {
  ESPECIFICO:              { label: 'Tipologia específica',             cor: 'emerald' },
  GENERICO_COM_FINALIDADE: { label: 'Termo com finalidade',             cor: 'amber'   },
  GENERICO_PURO:           { label: 'Termo genérico',                   cor: 'red'     },
  SOLO:                    { label: 'Termo descreve solo',              cor: 'blue'    },
  AMBIGUO_QUALIFICADO:     { label: 'Termo ambíguo',                    cor: 'amber'   },
  DESCONHECIDO:            { label: 'Termo não reconhecido',            cor: 'slate'   },
  VAZIO:                   { label: 'Sem tipologia registral',          cor: 'slate'   },
};